import { useMemo } from 'react';
import constants from '../../../../../config/constants';
import { getMetaKey } from '../../../common';

const { AVAILABILITY_STOCKINSTORE, PRODUCT_TYPE } = constants;

/**
 * Finds matching badges based on text content using regex patterns
 * @param {Object} item - Availability item with text content
 * @param {Array} badgeItems - Array of badge objects
 * @returns {Array} - Array of matching badges
 */
const findMatchingBadge = (item, badgeItems) => {
  if (!Array.isArray(badgeItems) || !item) {
    return [];
  }

  const matchingBadges = [];

  const deliveryPatterns = /\b(llega|envío|envio|delivery|entrega)\b/i;
  const pickupPatterns = /\b(retira|retiro|pickup|recoge|recoger)\b/i;

  badgeItems.forEach((badge) => {
    const badgeText = (badge?.label || badge?.text || '').toLowerCase().replace(/<[^>]*>/g, ''); // Remove HTML tags

    if (item?.shippingOptionType === 'HomeDelivery') {
      if (deliveryPatterns.test(badgeText)) {
        matchingBadges.push(badge);
      }
    }

    if (item?.shippingOptionType === 'PickupInStore') {
      if (pickupPatterns.test(badgeText)) {
        matchingBadges.push(badge);
      }
    }
  });

  const uniqueBadges = matchingBadges.filter(
    (badge, index, array) => array.findIndex((b) => (b?.type || b?.id) === (badge?.type || badge?.id)) === index
  );

  return uniqueBadges;
};

/**
 * Custom hook that encapsulates all business logic for AvailabilityDesktop
 * @param {Object} availabilityCommonProps - Component props
 * @returns {Object} - Processed data and computed values
 */
export const useAvailabilityDesktop = (availabilityCommonProps) => {
  const {
    availabilityList,
    labelsData,
    sizes,
    colorVariants,
    deliveryData,
    deliveryActions,
    selectedType,
    setSelectedType,
    textGenerated,
    deliveryErrors,
    selectedDeliveryOption,
    setSelectedDeliveryOption,
    zoneConfig,
    setZoneConfig,
    productData,
    variantData,
    appCtxData,
    badgeItems,
  } = availabilityCommonProps;

  const productTypeInfo = useMemo(() => {
    const { SOFT_GOOD, GIFTCARD } = PRODUCT_TYPE;
    const productType = productData?.productType;

    const isSoftProduct = productType === SOFT_GOOD;
    const isGiftCard = productType === GIFTCARD;
    const isSoftOrGiftCard = isSoftProduct || isGiftCard;

    return {
      isSoftProduct,
      isGiftCard,
      isSoftOrGiftCard,
    };
  }, [productData?.productType]);

  // Top specifications validation using optional chaining
  const hasTopSpecifications = useMemo(() => {
    const topSpecs = productData?.attributes?.topSpecifications ?? [];
    return Array.isArray(topSpecs) && topSpecs.length > 0;
  }, [productData?.attributes?.topSpecifications]);

  const processedAvailabilityList = useMemo(() => {
    if (!Array.isArray(availabilityList)) {
      return [];
    }

    const filteredItems = availabilityList
      .map((item, index) => {
        // Handle null/undefined items
        if (!item || !item.shippingOptionType) {
          return null;
        }

        // Skip non-store items for soft/gift card products
        if (productTypeInfo.isSoftOrGiftCard && item.shippingOptionType !== AVAILABILITY_STOCKINSTORE) {
          return null;
        }

        const { labelsKey } = getMetaKey(item.shippingOptionType);

        return {
          ...item,
          index,
          labelsKey,
          isLast: false,
          hasModal: index !== 3,
        };
      })
      .filter(Boolean); // Remove null items using native filter

    // Add shouldShowDivider based on filtered length and position
    return filteredItems.map((item, filteredIndex) => ({
      ...item,
      shouldShowDivider: filteredIndex === 1 && filteredItems.length >= 3,
    }));
  }, [availabilityList, productTypeInfo.isSoftOrGiftCard]);

  // Container CSS classes using template literals
  const containerClassName = useMemo(() => {
    const baseClass = 'availability-container';
    const modifierClass = !hasTopSpecifications ? 'new-hd-options' : '';

    return `${baseClass} ${modifierClass}`.trim();
  }, [hasTopSpecifications]);

  const getItemProps = useMemo(
    () => (item) => {
      return {
        key: item.shippingOptionType,
        shippingOptionType: item.shippingOptionType,
        labelsData,
        availability: item,
        isLast: item.isLast,
        hasModal: item.hasModal,
        sizes,
        colorVariants,
        availabilityList,
        deliveryData,
        deliveryActions,
        selectedType,
        setSelectedType,
        textGenerated,
        deliveryErrors,
        productData,
        variantData,
        appCtxData,
        selectedDeliveryOption,
        setSelectedDeliveryOption,
        zoneConfig,
        setZoneConfig,
        labelsKey: item.labelsKey,
        badges: findMatchingBadge(item, badgeItems),
      };
    },
    [
      badgeItems,
      labelsData,
      sizes,
      colorVariants,
      availabilityList,
      deliveryData,
      deliveryActions,
      selectedType,
      setSelectedType,
      textGenerated,
      deliveryErrors,
      productData,
      variantData,
      appCtxData,
      selectedDeliveryOption,
      setSelectedDeliveryOption,
      zoneConfig,
      setZoneConfig,
    ]
  );

  // Computed values for rendering decisions
  const computedValues = useMemo(
    () => ({
      shouldRender: Array.isArray(availabilityList) && availabilityList.length > 0,
      shouldShowDownloadable: productTypeInfo.isSoftOrGiftCard,
      hasAvailabilityItems: processedAvailabilityList.length > 0,
    }),
    [availabilityList, productTypeInfo.isSoftOrGiftCard, processedAvailabilityList.length]
  );

  return {
    // Product type information
    ...productTypeInfo,

    // Processed data
    processedAvailabilityList,
    hasTopSpecifications,
    containerClassName,

    // Helper functions
    getItemProps,

    // Computed values
    ...computedValues,

    // Labels data for divider
    labelsData,
  };
};
